import React, { useState, useCallback } from 'react';
import { useFetch } from '../hooks/useFetch';
import { userAPI } from '../api/client';
import { Card, Badge, Spinner, EmptyState, fmtDate } from '../components/UI';

const ACTION_META = {
  task_completed:        { icon:'✅', color:'#dcfce7', label:'Task Completed' },
  payment_sent:          { icon:'💰', color:'#dcfce7', label:'Payment Received' },
  withdrawal_requested:  { icon:'📤', color:'#fef3c7', label:'Withdrawal Requested' },
  withdrawal_approved:   { icon:'🎉', color:'#dcfce7', label:'Withdrawal Approved' },
  withdrawal_rejected:   { icon:'❌', color:'#fee2e2', label:'Withdrawal Rejected' },
  earnings_added:        { icon:'💵', color:'#eef1ff', label:'Earnings Added' },
  account_created:       { icon:'👋', color:'#eef1ff', label:'Account Created' },
  login:                 { icon:'🔑', color:'#f0f2f8', label:'Login' },
};

export default function Activity() {
  const [page, setPage]     = useState(1);
  const [filter, setFilter] = useState('all');

  const fetchActivity = useCallback(() => userAPI.activity(page), [page]);
  const { data, loading, error } = useFetch(fetchActivity);

  const all        = data?.activities || [];
  const pagination = data?.pagination  || {};
  const activities = filter === 'all' ? all : all.filter(a => a.action_type === filter);

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Activity Feed</h1>
          <p style={styles.sub}>A full log of work completed, payments, and account updates.</p>
        </div>
      </div>

      {/* Filter pills */}
      <div style={styles.filters}>
        {[['all','All'],['task_completed','Tasks'],['payment_sent','Payments'],['withdrawal_requested','Withdrawals'],['earnings_added','Earnings']].map(([val, label]) => (
          <button key={val} onClick={() => { setFilter(val); setPage(1); }}
            style={{ ...styles.filterBtn, ...(filter === val ? styles.filterActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      <Card>
        {loading ? <Spinner /> : error ? (
          <p style={{ color:'#dc2626', padding:16 }}>{error}</p>
        ) : activities.length === 0 ? (
          <EmptyState icon="📭" message="No activity found." />
        ) : (
          <>
            <div style={styles.timeline}>
              {activities.map((a, i) => {
                const meta = ACTION_META[a.action_type] || { icon:'📌', color:'#f0f2f8', label: a.action_type };
                return (
                  <div key={a.id} style={styles.timelineItem}>
                    <div style={styles.timelineLeft}>
                      <div style={{ ...styles.dot, background: meta.color, border: `2px solid ${meta.color === '#f0f2f8' ? '#e2e6f0' : meta.color}` }}>
                        <span style={{ fontSize:12 }}>{meta.icon}</span>
                      </div>
                      {i < activities.length - 1 && <div style={styles.line} />}
                    </div>
                    <div style={styles.timelineContent}>
                      <div style={styles.timelineHeader}>
                        <div>
                          <span style={{ fontSize:12, fontWeight:700, color:'#9099b4', textTransform:'uppercase', letterSpacing:'.4px' }}>{meta.label}</span>
                          <p style={styles.timelineDesc}>{a.description}</p>
                        </div>
                        <span style={styles.timelineDate}>{fmtDate(a.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {pagination.total > pagination.limit && (
              <div style={styles.pagination}>
                <button style={styles.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span style={{ fontSize:13, color:'#636b89' }}>Page {page} of {Math.ceil(pagination.total / pagination.limit)}</span>
                <button style={styles.pageBtn} disabled={page * pagination.limit >= pagination.total} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

const styles = {
  wrap:            { padding:'32px', fontFamily:"'DM Sans',sans-serif" },
  header:          { marginBottom:20 },
  h1:              { fontFamily:"'Sora',sans-serif", fontSize:22, fontWeight:700, color:'#1a1f5e', margin:0 },
  sub:             { fontSize:14, color:'#9099b4', marginTop:4 },
  filters:         { display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' },
  filterBtn:       { padding:'7px 16px', borderRadius:20, border:'1.5px solid #e2e6f0', background:'#fff', fontSize:13, fontWeight:500, cursor:'pointer', color:'#636b89', transition:'all .15s' },
  filterActive:    { background:'#eef1ff', borderColor:'#4f6ef7', color:'#4f6ef7', fontWeight:700 },
  timeline:        { padding:'8px 0' },
  timelineItem:    { display:'flex', gap:16, marginBottom:0 },
  timelineLeft:    { display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 },
  dot:             { width:36, height:36, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 },
  line:            { width:2, flex:1, background:'#f0f2f8', minHeight:16, margin:'4px 0' },
  timelineContent: { flex:1, paddingBottom:20 },
  timelineHeader:  { display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 },
  timelineDesc:    { fontSize:14, color:'#2e3456', marginTop:4, lineHeight:1.55 },
  timelineDate:    { fontSize:12, color:'#9099b4', whiteSpace:'nowrap', flexShrink:0, marginTop:2 },
  pagination:      { display:'flex', alignItems:'center', justifyContent:'center', gap:20, paddingTop:16 },
  pageBtn:         { padding:'7px 16px', border:'1.5px solid #e2e6f0', borderRadius:8, background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', color:'#636b89' },
};
